import React, { useState, useEffect, useCallback, useRef } from "react";
import { Building2, User, Clock, AlertCircle, FileText, Download } from "lucide-react";
import { ProjectService } from "../services/projectService";
import { supabase } from "../lib/supabase";
import MultiSelectDropdown from "./MultiSelectDropdown";
import { buildStructuredEmail } from "../utils/emailHtmlFormatter";
import { formatJobRevNumber } from '../utils/projectFormatting';
import { Modal, ModalBody, ModalFooter, Button } from "./ui";
import "./HoldProjectModal.css";
import "./ExportAndSend/ExportForm.css";

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatDateShort = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const month = date.getMonth() + 1; // getMonth() returns 0-11
  const day = date.getDate();
  return `${month}/${day}`;
};

const calculateDaysUntilDue = (createdAt, dueDate) => {
  if (!createdAt || !dueDate) return 0;
  const created = new Date(createdAt);
  const due = new Date(dueDate);
  const diffTime = due - created;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const RushProjectReminderModal = ({ project: initialProject, rushTracking, isOpen, onClose, onSuccess }) => {
  const [project, setProject] = useState(initialProject);
  const [sendEmail, setSendEmail] = useState(true);
  const [formData, setFormData] = useState({
    emailPreview: "",
    emailHtml: "",
    emailTo: [],
    emailCc: [],
    emailSubject: "",
    customMessage: "",
  });

  const [clientContacts, setClientContacts] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const htmlUpdateTimerRef = useRef(null);

  const daysUntilDue = calculateDaysUntilDue(project?.created_at, project?.due_date);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && initialProject?.id) {
      // Reload project to get latest data
      loadFreshProjectData(initialProject.id);
      loadCurrentUser();

      // Load contacts if client ID is available
      const clientId = initialProject?.clientId || initialProject?.client_id;
      if (clientId) {
        loadContacts(clientId);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialProject]);

  const loadFreshProjectData = async (projectId) => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          client:client_id(id, name),
          contact:contact_id(id, name, email)
        `)
        .eq('id', projectId)
        .single();

      if (error) throw error;
      console.log("📥 Loaded fresh project data:", data);
      setProject(data);
    } catch (err) {
      console.error('Error loading fresh project data:', err);
      setProject(initialProject);
    }
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && initialProject) {
      // Set default email toggle
      setSendEmail(true);

      // Auto-fill "To" field with project contact if available
      const contactId = initialProject?.contactId || initialProject?.contact_id;
      const defaultEmailTo = contactId ? [contactId.toString()] : [];

      // Pre-fill custom message with default text about rush deadline confirmation
      const finishGradeDueDate = initialProject?.finish_grade_due_date || initialProject?.finishGradeDueDate || initialProject?.due_date;
      const formattedDate = formatDateShort(finishGradeDueDate);
      const defaultMessage = `We wanted to confirm the deadline for this project. Is this truly a rush project that needs to be completed by ${formattedDate}, or would it be possible to extend the deadline?\n\nPlease let us know at your earliest convenience so we can prioritize our schedule accordingly.`;

      setFormData({
        emailPreview: "",
        emailHtml: "",
        emailTo: defaultEmailTo,
        emailCc: [],
        emailSubject: `${initialProject?.name || initialProject?.projectName} Due Date`,
        customMessage: defaultMessage,
      });

      setError(null);
    }
  }, [isOpen, initialProject]);

  // Auto-generate email when email is enabled
  useEffect(() => {
    if (sendEmail && project && currentUser && !formData.emailPreview) {
      const timer = setTimeout(() => {
        handlePrepareEmail();
      }, 200);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendEmail, project, currentUser]);

  // Auto-regenerate email HTML when custom message changes (debounced)
  useEffect(() => {
    if (!sendEmail || !formData.emailPreview) return;

    if (htmlUpdateTimerRef.current) {
      clearTimeout(htmlUpdateTimerRef.current);
    }

    htmlUpdateTimerRef.current = setTimeout(() => {
      updateEmailHTML();
    }, 1000);

    return () => {
      if (htmlUpdateTimerRef.current) {
        clearTimeout(htmlUpdateTimerRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.customMessage, formData.emailPreview, sendEmail]);

  const loadContacts = async (clientId) => {
    try {
      setLoadingContacts(true);
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('client_id', clientId);

      if (error) throw error;
      setClientContacts(data || []);
    } catch (err) {
      console.error('Error loading contacts:', err);
      setClientContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        const { data: userData, error: profileError } = await supabase
          .from('employees')
          .select('*')
          .eq('email', user.email)
          .single();

        if (!profileError && userData) {
          setCurrentUser(userData);
        }
      }
    } catch (err) {
      console.error('Error loading current user:', err);
    }
  };

  const updateEmailHTML = useCallback(() => {
    if (!formData.emailPreview) return;

    try {
      // Get contact first name for personalized greeting
      const primaryContact = clientContacts.find(contact =>
        formData.emailTo.length > 0 && contact.id === parseInt(formData.emailTo[0])
      );
      const contactName = primaryContact?.name || '';
      const contactFirstName = contactName.split(' ')[0] || contactName;

      // Parse the email preview to extract the greeting
      const previewLines = formData.emailPreview.split('\n').filter(line => line.trim());
      const greeting = contactFirstName ? `${contactFirstName},` : (previewLines.length > 0 && previewLines[0].includes(',') ? previewLines[0] : 'Hello,');

      // Generate HTML using buildStructuredEmail
      const htmlContent = buildStructuredEmail({
        greeting: greeting,
        templateMessages: [],
        discrepancies: [],
        customMessage: formData.customMessage || "",
        signature: currentUser?.email_signature_html || "",
      });

      setFormData(prev => ({
        ...prev,
        emailHtml: htmlContent,
      }));
    } catch (error) {
      console.error('Error formatting rush project email HTML:', error);
    }
  }, [formData.emailPreview, formData.customMessage, formData.emailTo, currentUser, clientContacts]);

  const handlePrepareEmail = useCallback(() => {
    if (!project || !currentUser) return;

    const projectName = project.name || project.projectName || 'Unknown Project';
    const jobNumber = project.job_number || project.jobNumber || 'N/A';
    const clientName = project.client?.name || project.clientName || 'Client';
    const dueDate = project.due_date || project.dueDate;
    const createdAt = project.created_at;

    // Get contact first name for personalized greeting
    const primaryContact = clientContacts.find(contact =>
      formData.emailTo.length > 0 && contact.id === parseInt(formData.emailTo[0])
    );
    const contactName = primaryContact?.name || project?.contact?.name || project?.contactName || "";
    const contactFirstName = contactName.split(" ")[0] || contactName;

    // Build email subject
    const subject = `${projectName} Due Date`;

    // Build plain text preview for storage (backwards compatibility)
    let emailPreview = `${contactFirstName || 'Hello'},\n\n`;
    emailPreview += `Project "${projectName}" (Job #${jobNumber}) is flagged as a rush project with only ${daysUntilDue} day${daysUntilDue !== 1 ? 's' : ''} until the due date.\n\n`;
    emailPreview += `Project Details:\n`;
    emailPreview += `- Project Name: ${projectName}\n`;
    emailPreview += `- Job Number: ${jobNumber}\n`;
    emailPreview += `- Client: ${clientName}\n`;
    emailPreview += `- Date Submitted: ${formatDate(createdAt)}\n`;
    emailPreview += `- Due Date: ${formatDate(dueDate)}\n`;
    emailPreview += `- Days Until Due: ${daysUntilDue} days\n\n`;

    // Generate HTML using buildStructuredEmail
    const htmlContent = buildStructuredEmail({
      greeting: `${contactFirstName || 'Hello'},`,
      templateMessages: [],
      discrepancies: [],
      customMessage: formData.customMessage || "",
      signature: currentUser?.email_signature_html || "",
    });

    setFormData(prev => ({
      ...prev,
      emailSubject: subject,
      emailPreview: emailPreview,
      emailHtml: htmlContent,
    }));

  }, [project, currentUser, formData.customMessage, formData.emailTo, clientContacts, daysUntilDue]);

  const handleDismiss = async () => {
    try {
      setSaving(true);
      setError(null);

      // Update tracking record to mark as dismissed (removes from Rush section)
      await supabase
        .from('rush_project_email_tracking')
        .update({
          email_status: 'dismissed',
        })
        .eq('project_id', project.id);

      console.log("✅ Rush project dismissed successfully");

      if (onSuccess) {
        await onSuccess();
      }

      onClose();
    } catch (err) {
      console.error("Error dismissing rush project:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    try {
      setSaving(true);
      setError(null);

      // Check recipients
      if (!formData.emailTo || formData.emailTo.length === 0) {
        throw new Error('Please select at least one recipient');
      }

      // Get contact emails formatted as "Name <email>" (matching hold email pattern)
      const toEmails = formData.emailTo
        .map(contactId => {
          const contact = clientContacts.find(c => c.id === parseInt(contactId));
          if (!contact?.email) return null;
          // Format as "Name <email>" if name exists, otherwise just email
          return contact.name
            ? `${contact.name} <${contact.email}>`
            : contact.email;
        })
        .filter(Boolean);

      const ccEmails = formData.emailCc
        .map(contactId => {
          const contact = clientContacts.find(c => c.id === parseInt(contactId));
          if (!contact?.email) return null;
          // Format as "Name <email>" if name exists, otherwise just email
          return contact.name
            ? `${contact.name} <${contact.email}>`
            : contact.email;
        })
        .filter(Boolean);

      // Send email via service (which triggers n8n workflow)
      await ProjectService.sendRushProjectEmail(
        project.id,
        {
          to: toEmails,
          cc: ccEmails,
          subject: formData.emailSubject,
          html: formData.emailHtml,
          preview: formData.emailPreview,
        },
        currentUser?.email
      );

      console.log("✅ Rush project email sent successfully");

      if (onSuccess) {
        await onSuccess();
      }

      onClose();
    } catch (err) {
      console.error("Error sending rush project email:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileDownload = async (url, filename) => {
    if (!url) {
      console.error("No URL provided for download");
      return;
    }

    try {
      // Open the file in a new tab
      window.open(url, '_blank');
    } catch (error) {
      console.error("Error downloading file:", error);
      setError("Failed to download file");
    }
  };

  if (!isOpen) return null;

  return (
    <Modal trackAs="RushProjectReminderModal"
      isOpen={isOpen}
      onClose={onClose}
      size={sendEmail ? 'xlarge' : 'lg'}
    >
      {/* Custom Header - Match HoldProjectModal */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-400)',
              flexShrink: 0,
            }}
          >
            {formatJobRevNumber(project?.jobNumber || project?.job_number, project?.rev)}
          </span>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--gray-50)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
              minWidth: 0,
            }}
          >
            {project?.projectName || project?.name}
          </h1>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); sendEmail ? handleSend() : handleDismiss(); }} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <ModalBody>
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--danger)',
              fontSize: 'var(--text-sm)',
            }}>
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Project Context Bar - Horizontal Layout (matching Hold modal) */}
          <div className="export-form-section" style={{ marginBottom: 'var(--space-6)' }}>
            <h3 style={{ marginBottom: 'var(--space-4)' }}>Project Context</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 'var(--space-3)',
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-surface)',
              border: '1px solid var(--gray-700)',
              borderRadius: 'var(--radius-lg)',
            }}>
              {/* Client */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  color: 'var(--gray-400)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-medium)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <Building2 size={12} style={{ display: 'inline', marginRight: '4px' }} />
                  Client
                </label>
                <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                  {project?.client?.name || project?.clientName || 'Not Specified'}
                </div>
              </div>

              {/* Contact */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  color: 'var(--gray-400)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-medium)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <User size={12} style={{ display: 'inline', marginRight: '4px' }} />
                  Contact
                </label>
                <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                  {project?.contact?.name || project?.contactName || 'Not Specified'}
                </div>
              </div>

              {/* Modeling Status */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  color: 'var(--gray-400)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-medium)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Status
                </label>
                <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                  {project?.modeling_status || project?.modelingStatus || 'Not Specified'}
                </div>
              </div>

              {/* Due Date */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  color: 'var(--gray-400)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-medium)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <Clock size={12} style={{ display: 'inline', marginRight: '4px' }} />
                  Due Date
                </label>
                <div style={{ color: 'rgb(234, 179, 8)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                  {formatDate(project?.due_date || project?.dueDate)} ({daysUntilDue} days)
                </div>
              </div>
            </div>
          </div>

          {/* Project Details Section */}
          <div className="export-form-section" style={{ marginBottom: 'var(--space-6)' }}>
            <h3 style={{ marginBottom: 'var(--space-4)' }}>Project Details</h3>
            <div style={{
              padding: 'var(--space-4)',
              backgroundColor: 'var(--dark-surface)',
              border: '1px solid var(--gray-700)',
              borderRadius: 'var(--radius-lg)',
            }}>
              {/* First Row: Acreage and File Attachments */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
                {/* Approximate Acreage */}
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: 'var(--gray-300)',
                  }}>
                    Approximate Acreage
                  </label>
                  <div style={{
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--dark-elevated)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--gray-200)',
                    fontSize: 'var(--text-sm)',
                  }}>
                    {project?.approximate_acreage ? `${project.approximate_acreage} acres` : 'Not specified'}
                  </div>
                </div>

              </div>

              {/* Second Row: PDFs and CAD Files Received */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
                {/* PDFs Received */}
                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--dark-elevated)',
                    border: `1px solid ${project?.pdfs_received_date ? 'var(--cta-blue)' : 'var(--gray-700)'}`,
                    borderRadius: 'var(--radius-md)',
                    pointerEvents: 'none',
                  }}>
                    <input
                      type="checkbox"
                      checked={!!project?.pdfs_received_date}
                      readOnly
                      style={{ marginRight: 'var(--space-2)', pointerEvents: 'none' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        color: project?.pdfs_received_date ? 'var(--cta-blue)' : 'var(--gray-300)',
                      }}>
                        PDFs Received
                      </div>
                      {project?.pdfs_received_date && (
                        <div style={{
                          fontSize: 'var(--text-xs)',
                          color: 'var(--gray-400)',
                          marginTop: 'var(--space-1)',
                        }}>
                          {formatDate(project.pdfs_received_date)}
                        </div>
                      )}
                    </div>
                  </label>
                </div>

                {/* CAD Files Received */}
                <div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--dark-elevated)',
                    border: `1px solid ${project?.cad_files_received_date ? 'var(--cta-blue)' : 'var(--gray-700)'}`,
                    borderRadius: 'var(--radius-md)',
                    pointerEvents: 'none',
                  }}>
                    <input
                      type="checkbox"
                      checked={!!project?.cad_files_received_date}
                      readOnly
                      style={{ marginRight: 'var(--space-2)', pointerEvents: 'none' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                        color: project?.cad_files_received_date ? 'var(--cta-blue)' : 'var(--gray-300)',
                      }}>
                        CAD Files Received
                      </div>
                      {project?.cad_files_received_date && (
                        <div style={{
                          fontSize: 'var(--text-xs)',
                          color: 'var(--gray-400)',
                          marginTop: 'var(--space-1)',
                        }}>
                          {formatDate(project.cad_files_received_date)}
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              {/* File Attachments */}
              {project?.file_attachments && project.file_attachments.length > 0 && (
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: 'var(--space-2)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: 'var(--gray-300)',
                  }}>
                    <FileText size={12} style={{ display: 'inline', marginRight: '4px' }} />
                    File Attachments ({project.file_attachments.length})
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    {project.file_attachments.map((file, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: 'var(--space-2) var(--space-3)',
                          backgroundColor: 'var(--dark-elevated)',
                          border: '1px solid var(--gray-700)',
                          borderRadius: 'var(--radius-md)',
                          transition: 'all 0.15s ease',
                          cursor: 'pointer',
                        }}
                        onClick={() => handleFileDownload(
                          file.url || file.path || file,
                          file.filename || file.name || `Attachment ${index + 1}`
                        )}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = 'var(--gray-600)';
                          e.currentTarget.style.backgroundColor = 'var(--gray-800)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'var(--gray-700)';
                          e.currentTarget.style.backgroundColor = 'var(--dark-elevated)';
                        }}
                      >
                        <Download size={14} style={{ marginRight: 'var(--space-2)', color: 'var(--cta-blue)' }} />
                        <span style={{
                          fontSize: 'var(--text-sm)',
                          color: 'var(--gray-200)',
                          flex: 1,
                        }}>
                          {file.filename || file.name || `Attachment ${index + 1}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Model Request Info Section */}
          {(project?.model_request_additional_info || project?.modelRequestAdditionalInfo || (project?.additional_requests && project.additional_requests.length > 0) || (project?.additionalRequests && project.additionalRequests.length > 0)) && (
            <div style={{ marginBottom: 'var(--space-6)' }}>
              <h2 style={{
                margin: 0,
                marginBottom: 'var(--space-4)',
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-200)',
              }}>
                Model Request Info
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {/* Model Request Additional Info */}
                {(project?.model_request_additional_info || project?.modelRequestAdditionalInfo) && (
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                      color: 'var(--gray-300)',
                    }}>
                      Model Request Additional Info
                    </label>
                    <div style={{
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--gray-200)',
                      fontSize: 'var(--text-sm)',
                      minHeight: '80px',
                      whiteSpace: 'pre-wrap',
                    }}>
                      {project.model_request_additional_info || project.modelRequestAdditionalInfo}
                    </div>
                  </div>
                )}

                {/* Additional Requests */}
                {((project?.additional_requests && project.additional_requests.length > 0) || (project?.additionalRequests && project.additionalRequests.length > 0)) && (
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: 'var(--space-2)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-medium)',
                      color: 'var(--gray-300)',
                    }}>
                      Additional Requests
                    </label>
                    <div style={{
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--dark-elevated)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-md)',
                    }}>
                      <ul style={{
                        margin: 0,
                        paddingLeft: 'var(--space-5)',
                        color: 'var(--gray-200)',
                        fontSize: 'var(--text-sm)',
                      }}>
                        {(project.additional_requests || project.additionalRequests).map((request, index) => (
                          <li key={index} style={{ marginBottom: 'var(--space-1)' }}>
                            {request}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Send Email Toggle - Matching Hold Modal Pattern */}
        <div className="export-form-section" style={{ marginBottom: 'var(--space-6)' }}>
          <h3 style={{ marginBottom: 'var(--space-4)' }}>Email Configuration</h3>
          <div style={{
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-surface)',
            border: '1px solid var(--gray-700)',
            borderRadius: 'var(--radius-lg)',
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: 'var(--space-2)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-medium)',
                color: 'var(--gray-300)',
              }}>
                Do you want to send an email about this rush project?
              </label>
              <div style={{
                display: 'inline-flex',
                padding: '2px',
                backgroundColor: 'var(--dark-elevated)',
                border: '1px solid var(--gray-700)',
                borderRadius: 'var(--radius-md)',
                width: 'fit-content',
              }}>
                <button
                  type="button"
                  onClick={() => setSendEmail(true)}
                  style={{
                    padding: '6px 20px',
                    border: 'none',
                    borderRadius: 'calc(var(--radius-md) - 2px)',
                    backgroundColor: sendEmail ? 'var(--cta-blue)' : 'transparent',
                    color: sendEmail ? 'white' : 'var(--gray-400)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setSendEmail(false)}
                  style={{
                    padding: '6px 20px',
                    border: 'none',
                    borderRadius: 'calc(var(--radius-md) - 2px)',
                    backgroundColor: !sendEmail ? 'var(--cta-blue)' : 'transparent',
                    color: !sendEmail ? 'white' : 'var(--gray-400)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Email Recipients and Composition - Only show if email enabled */}
        {sendEmail && (
          <>
            {/* Email Recipients Section */}
            <div className="export-form-section">
              <h3>Email Recipients</h3>
              <div className="export-recipients-section">
                <p style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                  margin: '0 0 var(--space-4) 0'
                }}>
                  Select contacts for rush project deadline confirmation email
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
                  {/* Left: To and CC Fields Stacked */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                    {/* Email To Field */}
                    <div className="export-email-field">
                      <label className="export-email-field-label">
                        To: <span className="required">*</span>
                      </label>
                      <div className="export-email-field-input">
                        <MultiSelectDropdown
                          options={clientContacts.map(c => c.name + (c.email ? ` (${c.email})` : ""))}
                          selected={formData.emailTo
                            .map((id) => {
                              const c = clientContacts.find(contact => contact.id === parseInt(id));
                              return c ? c.name + (c.email ? ` (${c.email})` : "") : "";
                            })
                            .filter(Boolean)}
                          onChange={(selectedNames) => {
                            const selectedIds = selectedNames
                              .map((name) => {
                                const c = clientContacts.find(contact => contact.name + (contact.email ? ` (${contact.email})` : "") === name);
                                return c ? c.id.toString() : null;
                              })
                              .filter(Boolean);
                            handleInputChange("emailTo", selectedIds);
                          }}
                          placeholder="Select email recipients..."
                          disabled={loadingContacts}
                        />
                      </div>
                    </div>

                    {/* Email CC Field */}
                    <div className="export-email-field">
                      <label className="export-email-field-label">CC:</label>
                      <div className="export-email-field-input">
                        <MultiSelectDropdown
                          options={clientContacts
                            .filter(c => !formData.emailTo.includes(c.id.toString()))
                            .map(c => c.name + (c.email ? ` (${c.email})` : ""))}
                          selected={formData.emailCc
                            .map((id) => {
                              const c = clientContacts.find(contact => contact.id === parseInt(id));
                              return c ? c.name + (c.email ? ` (${c.email})` : "") : "";
                            })
                            .filter(Boolean)}
                          onChange={(selectedNames) => {
                            const selectedIds = selectedNames
                              .map((name) => {
                                const c = clientContacts.find(contact => contact.name + (contact.email ? ` (${contact.email})` : "") === name);
                                return c ? c.id.toString() : null;
                              })
                              .filter(Boolean);
                            handleInputChange("emailCc", selectedIds);
                          }}
                          placeholder="Add CC recipients (optional)..."
                          disabled={loadingContacts}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right: Empty space for symmetry */}
                  <div></div>
                </div>
              </div>
            </div>

            {/* Email Content Section */}
            <div className="export-form-section">
              <h3 style={{ marginBottom: 'var(--space-4)' }}>Email Content</h3>

              {/* Subject Field */}
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label htmlFor="emailSubject" style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)',
                }}>
                  Subject
                </label>
                <input
                  type="text"
                  id="emailSubject"
                  value={formData.emailSubject}
                  onChange={(e) => handleInputChange("emailSubject", e.target.value)}
                  placeholder="Email subject..."
                  style={{
                    width: '100%',
                    padding: 'var(--space-3)',
                    backgroundColor: 'var(--dark-elevated)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--gray-200)',
                    fontSize: 'var(--text-sm)',
                    fontFamily: 'inherit',
                    transition: 'border-color var(--transition-fast)',
                  }}
                  onFocus={(e) => e.currentTarget.style.borderColor = 'var(--cta-blue)'}
                  onBlur={(e) => e.currentTarget.style.borderColor = 'var(--gray-700)'}
                />
              </div>

              {/* Two-column layout: Custom Message + Preview */}
              <div className="export-email-container">
                {/* Left Side - Email Editor with Custom Message */}
                <div className="export-email-editor">
                  <div className="export-email-editor-header">
                    <h4>Email Message</h4>
                    <small>Edit the email body as needed</small>
                  </div>
                  <textarea
                    id="customMessage"
                    name="customMessage"
                    value={formData.customMessage}
                    onChange={(e) => handleInputChange("customMessage", e.target.value)}
                    placeholder="Email message body..."
                    className="export-email-body"
                  />
                </div>

                {/* Right Side - HTML Preview */}
                <div className="export-email-preview">
                  <div className="export-email-preview-header">
                    <h4>Email Preview</h4>
                    <small>How the email will appear</small>
                  </div>
                  <div className="export-email-preview-content">
                    <iframe
                      srcDoc={formData.emailHtml || '<div style="padding: 20px; text-align: center; color: #666;">Email preview will appear here</div>'}
                      title="Email Preview"
                      className="export-email-preview-iframe"
                      sandbox="allow-same-origin allow-scripts"
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        </ModalBody>

        {/* Footer */}
        <ModalFooter style={{ background: 'var(--dark-surface)', borderTop: '1px solid var(--gray-700)' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', width: '100%' }}>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          {sendEmail ? (
            <Button
              variant="primary"
              onClick={handleSend}
              disabled={saving || !formData.emailTo || formData.emailTo.length === 0}
            >
              {saving ? 'Sending...' : 'Send Email'}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleDismiss}
              disabled={saving}
            >
              {saving ? 'Processing...' : 'Dismiss Reminder'}
            </Button>
          )}
        </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default RushProjectReminderModal;
